import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Button, Snackbar } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../components/AdminContentWrapper';
import UserProfileService from '../../../services/UserProfileService';
import SemanticDatabaseService from '../../../services/SemanticDatabaseService';
import SemanticTableService from '../../../services/SemanticTableService';
import {
  SemanticDatabaseSection,
  SemanticDatabaseFormDialog,
  SemanticDatabaseViewDialog,
  SemanticDatabaseDeleteDialog
} from '../../../components/semantic-database';

const resolveUserId = (profile) => profile?.user_id || profile?.userId || profile?.user_uuid;
const resolveAccountId = (profile) => profile?.account_id || profile?.accountUuid || profile?.account_uuid;

const asRecordList = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

const asTableList = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response?.semantic_tables)) {
    return response.semantic_tables;
  }
  return [];
};

const asRecord = (response) => {
  if (!response) {
    return null;
  }
  if (response.semantic_database_id) {
    return response;
  }
  if (response.data) {
    return asRecord(response.data);
  }
  if (response.item) {
    return asRecord(response.item);
  }
  return null;
};

const asUsersAllowedList = (response) => {
  let collection = [];
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    collection = response;
  } else if (Array.isArray(response?.items)) {
    collection = response.items;
  } else if (Array.isArray(response?.data)) {
    collection = response.data;
  } else if (Array.isArray(response?.users_allowed)) {
    collection = response.users_allowed;
  }

  return collection
    .map((entry) => {
      if (!entry) {
        return null;
      }
      if (typeof entry === 'string') {
        return { user_id: entry };
      }
      if (entry.user_id) {
        return { user_id: entry.user_id };
      }
      if (entry.userId) {
        return { user_id: entry.userId };
      }
      if (entry.id) {
        return { user_id: entry.id };
      }
      return null;
    })
    .filter(Boolean);
};

const asGroupsAllowedList = (response) => {
  let collection = [];
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    collection = response;
  } else if (Array.isArray(response?.items)) {
    collection = response.items;
  } else if (Array.isArray(response?.data)) {
    collection = response.data;
  } else if (Array.isArray(response?.users_groups_allowed)) {
    collection = response.users_groups_allowed;
  }

  return collection
    .map((entry) => {
      if (!entry) {
        return null;
      }
      if (typeof entry === 'string') {
        return { group_id: entry };
      }
      if (entry.group_id) {
        return { group_id: entry.group_id };
      }
      if (entry.groupId) {
        return { group_id: entry.groupId };
      }
      if (entry.id) {
        return { group_id: entry.id };
      }
      return null;
    })
    .filter(Boolean);
};

const SemanticDatabase = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [records, setRecords] = useState([]);
  // Solo para calcular la columna "Tablas" por base semántica -- no se
  // renderiza acá, el listado de tablas en sí vive en SemanticEntities.jsx.
  const [tables, setTables] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [connections, setConnections] = useState([]);
  const [isRefreshingConnections, setIsRefreshingConnections] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const userId = resolveUserId(profile);
  const accountId = resolveAccountId(profile);

  const connectionsIndex = useMemo(() => {
    const index = {};
    (connections || []).forEach((connection) => {
      if (connection?.connection_id) {
        index[connection.connection_id] = connection;
      }
    });
    return index;
  }, [connections]);

  const activeRecords = useMemo(() => {
    return (records || []).filter((item) => !item?.soft_delete?.deleted);
  }, [records]);

  const tableCountByDatabase = useMemo(() => {
    const counts = {};
    (tables || []).forEach((table) => {
      if (table?.soft_delete?.deleted) {
        return;
      }
      const databaseId = table?.semantic_database_id;
      if (!databaseId) {
        return;
      }
      counts[databaseId] = (counts[databaseId] || 0) + 1;
    });
    return counts;
  }, [tables]);

  const handleOpenTables = useCallback(
    (record) => {
      if (!record?.semantic_database_id) {
        return;
      }
      navigate(`/admin/home/semantic-models/base-datos/${record.semantic_database_id}/entidades`);
    },
    [navigate]
  );


  const refreshConnectionsList = useCallback(async () => {
    if (!userId) {
      setSnackbar({
        open: true,
        message: 'No pudimos identificar al usuario actual para recargar las conexiones.',
        severity: 'error'
      });
      return;
    }

    try {
      setIsRefreshingConnections(true);
      const connectionsList = await SemanticDatabaseService.listConnections(userId);
      setConnections(connectionsList || []);
    } catch (connectionsError) {
      console.error('Error al recargar conexiones activas:', connectionsError);
      setSnackbar({
        open: true,
        message: 'No fue posible actualizar el listado de conexiones.',
        severity: 'error'
      });
    } finally {
      setIsRefreshingConnections(false);
    }
  }, [userId]);


  const deletedRecords = useMemo(() => {
    return (records || []).filter((item) => item?.soft_delete?.deleted);
  }, [records]);

  const hydrateRecord = useCallback(
    async (record) => {
      if (!record?.semantic_database_id) {
        return record;
      }

      let detailedRecord = record;

      if (accountId) {
        try {
          const detailResponse = await SemanticDatabaseService.getById(
            accountId,
            record.semantic_database_id
          );
          detailedRecord = asRecord(detailResponse) || detailedRecord;
        } catch (detailError) {
          console.error('Error obteniendo detalle de la base semántica:', detailError);
        }
      }

      if (!Array.isArray(detailedRecord?.users_allowed) || detailedRecord.users_allowed.length === 0) {
        try {
          const usersResponse = await SemanticDatabaseService.getUsersAllowed(record.semantic_database_id);
          const usersAllowed = asUsersAllowedList(usersResponse);
          if (usersAllowed.length > 0) {
            detailedRecord = {
              ...detailedRecord,
              users_allowed: usersAllowed
            };
          }
        } catch (usersError) {
          console.error('Error cargando usuarios permitidos:', usersError);
        }
      }

      if (!Array.isArray(detailedRecord?.users_groups_allowed)) {
        try {
          const groupsResponse = await SemanticDatabaseService.getGroupsAllowed(record.semantic_database_id);
          const groupsAllowed = asGroupsAllowedList(groupsResponse);
          if (groupsAllowed.length > 0) {
            detailedRecord = {
              ...detailedRecord,
              users_groups_allowed: groupsAllowed
            };
          }
        } catch (groupsError) {
          console.error('Error cargando grupos permitidos:', groupsError);
        }
      }

      return detailedRecord;
    },
    [accountId]
  );

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const profileData = await UserProfileService.getProfile();
      if (!profileData) {
        throw new Error('No se encontró el perfil del usuario.');
      }

      setProfile(profileData);
      const resolvedUserId = resolveUserId(profileData);
      const resolvedAccountId = resolveAccountId(profileData);

      if (!resolvedUserId) {
        throw new Error('No pudimos determinar el usuario autenticado.');
      }
      if (!resolvedAccountId) {
        throw new Error('No pudimos determinar la cuenta asociada.');
      }

      let connectionsList = [];
      try {
        connectionsList = await SemanticDatabaseService.listConnections(resolvedUserId);
      } catch (connectionsError) {
        console.error('Error al cargar conexiones activas:', connectionsError);
        setSnackbar({
          open: true,
          message: 'No fue posible cargar el listado de conexiones. Algunas opciones podrían no mostrarse.',
          severity: 'warning'
        });
        connectionsList = [];
      }

      const databasesData = await SemanticDatabaseService.list(resolvedAccountId, { includeDeleted: true });

      let tablesData = [];
      try {
        tablesData = await SemanticTableService.list({ accountId: resolvedAccountId, includeDeleted: false });
      } catch (tablesError) {
        console.error('Error cargando el conteo de tablas por base semántica:', tablesError);
        tablesData = [];
      }

      setConnections(connectionsList);

      setRecords(asRecordList(databasesData));
      setTables(asTableList(tablesData));

    } catch (loadError) {
      console.error('Error cargando bases semánticas:', loadError);
      setError(loadError.message || 'No se pudieron cargar las bases semánticas.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshRecords = useCallback(async () => {
    if (!accountId) {
      return;
    }
    try {
      const databasesData = await SemanticDatabaseService.list(accountId, { includeDeleted: true });
      setRecords(asRecordList(databasesData));
      try {
        const tablesData = await SemanticTableService.list({ accountId, includeDeleted: false });
        setTables(asTableList(tablesData));
      } catch (tablesError) {
        console.error('Error actualizando el conteo de tablas por base semántica:', tablesError);
      }
    } catch (refreshError) {
      console.error('Error actualizando listado de bases semánticas:', refreshError);
      setSnackbar({
        open: true,
        message: 'No se pudo actualizar el listado de bases semánticas.',
        severity: 'error'
      });
    }
  }, [accountId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleOpenCreate = () => {
    setFormMode('create');
    setSelectedRecord(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = async (record) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setIsFormOpen(true);
    try {
      const hydrated = await hydrateRecord(record);
      if (hydrated) {
        setSelectedRecord(hydrated);
      }
    } catch (hydrateError) {
      console.error('Error preparando datos para edición:', hydrateError);
      setSnackbar({
        open: true,
        message: 'No se pudieron cargar todos los datos de la base semántica.',
        severity: 'warning'
      });
    }
  };

  const handleCloseForm = () => {
    if (isSaving) {
      return;
    }
    setIsFormOpen(false);
    setSelectedRecord(null);
  };

  const handleSubmitForm = async (formPayload) => {
    if (!userId || !accountId) {
      setSnackbar({
        open: true,
        message: 'No pudimos identificar al usuario o la cuenta actual.',
        severity: 'error'
      });
      return;
    }

    setIsSaving(true);

    const basePayload = {
      ...formPayload,
      account_id: accountId
    };

    try {
      if (formMode === 'edit' && selectedRecord?.semantic_database_id) {
        const updatePayload = {
          ...basePayload,
          semantic_database_id: selectedRecord.semantic_database_id
        };

        const updateResponse = await SemanticDatabaseService.update(updatePayload);

        const updatedRecord = asRecord(updateResponse) || {
          ...selectedRecord,
          ...updatePayload
        };

        setRecords((prev) =>
          prev.map((record) =>
            record.semantic_database_id === selectedRecord.semantic_database_id
              ? { ...record, ...updatedRecord }
              : record
          )
        );

        setSnackbar({ open: true, message: 'Base semántica actualizada con éxito.', severity: 'success' });
      } else {
        const createResponse = await SemanticDatabaseService.create(basePayload);
        const createdRecord = asRecord(createResponse) || null;

        if (createdRecord) {
          setRecords((prev) => [...prev, createdRecord]);
        } else {
          await refreshRecords();
        }

        setSnackbar({ open: true, message: 'Base semántica creada correctamente.', severity: 'success' });
      }

      setIsFormOpen(false);
      setSelectedRecord(null);
    } catch (submitError) {
      console.error('Error guardando base semántica:', submitError);
      setSnackbar({
        open: true,
        message: submitError.message || 'Ocurrió un error al guardar la base semántica.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenView = async (record) => {
    setViewRecord(record);
    try {
      const hydrated = await hydrateRecord(record);
      if (hydrated) {
        setViewRecord(hydrated);
      }
    } catch (hydrateError) {
      console.error('Error obteniendo detalle para la vista:', hydrateError);
    }
  };

  const handleCloseView = () => {
    setViewRecord(null);
  };

  const handleOpenDelete = (record) => {
    setDeleteRecord(record);
  };

  const handleCloseDelete = () => {
    if (isDeleting) {
      return;
    }
    setDeleteRecord(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRecord?.semantic_database_id) {
      return;
    }

    setIsDeleting(true);
    try {
      const deleteResponse = await SemanticDatabaseService.softDelete(
        deleteRecord.semantic_database_id
      );

      const deletedRecordResponse = asRecord(deleteResponse);

      if (deletedRecordResponse) {
        setRecords((prev) =>
          prev.map((record) =>
            record.semantic_database_id === deletedRecordResponse.semantic_database_id
              ? { ...record, ...deletedRecordResponse }
              : record
          )
        );
      } else {
        await refreshRecords();
      }

      setSnackbar({
        open: true,
        message: 'Se realizó el borrado lógico de la base semántica.',
        severity: 'success'
      });
    } catch (deleteError) {
      console.error('Error realizando soft delete:', deleteError);
      setSnackbar({
        open: true,
        message: deleteError.message || 'No se pudo eliminar la base semántica.',
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
      setDeleteRecord(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <PageContainer
      title="Base de datos semántica"
      description="Administra los modelos semánticos conectados a tus fuentes de datos y controla quién puede utilizarlos."
      actions={[
        <Button
          key="create"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{
            bgcolor: 'var(--wb-primary)',
            '&:hover': { bgcolor: 'var(--wb-primary-hover)' }
          }}
        >
          Nueva base
        </Button>
      ]}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      <SemanticDatabaseSection
        activeRecords={activeRecords}
        deletedRecords={deletedRecords}
        filter={filter}
        onFilterChange={setFilter}
        onCreate={handleOpenCreate}
        onView={handleOpenView}
        onEdit={handleOpenEdit}
        onDelete={handleOpenDelete}
        isLoading={isLoading}
        error={null}
        connectionsIndex={connectionsIndex}
        tableCountByDatabase={tableCountByDatabase}
        onOpenTables={handleOpenTables}
      />

      <SemanticDatabaseFormDialog
        open={isFormOpen}
        mode={formMode}
        initialValues={selectedRecord}
        connections={connections}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
        isSaving={isSaving}
        onRefreshConnections={userId ? refreshConnectionsList : undefined}
        isRefreshingConnections={isRefreshingConnections}
      />

      <SemanticDatabaseViewDialog
        open={Boolean(viewRecord)}
        onClose={handleCloseView}
        record={viewRecord}
        connection={viewRecord ? connectionsIndex[viewRecord.connection_id] : null}
      />

      <SemanticDatabaseDeleteDialog
        open={Boolean(deleteRecord)}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        record={deleteRecord}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default SemanticDatabase;
